import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';

/**
 * POST /api/Patients/CreateUpdatePerson — **inline create-appointment
 * patient-create only.**
 *
 * Mirrors Flutter `createUpdatePerson()` in
 * `patient_data_source_impl.dart:422`, which is dispatched exclusively
 * from `patient_create_widget.dart` inside the scheduling
 * create-appointment flow. The patient-menu Personal Details tab uses
 * a DIFFERENT endpoint — `POST /api/Patients` — wired through
 * `useCreatePatientFromMenu`. Don't mix them; the backend treats the
 * two requests differently (CreateUpdatePerson returns the abbreviated
 * `CreatePatientResponse` shape; `/api/Patients` returns a full
 * Patient).
 *
 * Wire-shape rules (verified against a working dev curl, 2026-05-13):
 *   - Every field the backend model binder reads MUST be present on
 *     the request — missing fields cause Guid/parser failures.
 *     Defaults below match the dev curl exactly (mostly nulls).
 *   - `gender` is the GUID from the Master Gender list. `genderName`
 *     is the full master record object `{ id, name }`.
 *   - `consent` ships a constant Opt-In object on create per the dev
 *     curl — there is no UI for it in the MVP form.
 *   - `birthDate` ships as `YYYY-MM-DDTHH:mm:ss.sss` (no `Z` suffix) —
 *     same .NET local-DateTime form as the insurance save.
 *
 * Returns the freshly-minted patient identifiers — caller back-fills
 * the create-appointment form with `patientId` so the new patient is
 * selected without a re-search.
 */

export interface CreatePatientResponse {
  patientId?: string;
  patientMrn?: string;
  lastUpdated?: string;
  status?: boolean;
}

interface GenderRef {
  id: string;
  name: string;
}

/** Fields the inline form supplies; everything else is defaulted to the
 *  null / constant shape the backend expects. */
export interface CreatePatientInput {
  firstName: string;
  middleName: string | null;
  lastName: string;
  /** Master-data Gender GUID. */
  gender: string;
  /** Master-data Gender record so the backend can echo it back. */
  genderName: GenderRef;
  /** "YYYY-MM-DDTHH:mm:ss.sss" — no `Z` (matches the dev curl). */
  birthDate: string;
  email: string | null;
  /** "+<dial>-<digits>" e.g. "+971-54354445". */
  phoneNumber: string;
  organizationId: string;
  practiceId: string;
  isAdult: boolean;
  isCreatedFromPatientMenu: boolean;
}

interface CreateUpdatePersonBody {
  email: string | null;
  isMissingData: boolean;
  importance: unknown | null;
  residencyType: unknown | null;
  consent: { definition: string; code: string; display: string };
  frontIdCardUrl: string | null;
  backIdCardUrl: string | null;
  phoneNumber: string;
  isAdult: boolean;
  organizationId: string;
  patientId: string | null;
  practiceId: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  birthDate: string;
  age: unknown | null;
  gender: string;
  genderName: GenderRef;
  languageName: unknown | null;
  timeZone: unknown | null;
  language: unknown | null;
  maritalStatus: unknown | null;
  interpreterRequired: boolean;
  photoUrl: string | null;
  idFrontImage: string | null;
  idBackImage: string | null;
  isActive: boolean | null;
  lastUpdated: string | null;
  patientMRN: string | null;
  identification: unknown | null;
  patientOccupation: unknown | null;
  patientEducation: unknown | null;
  guardianIdenitification: unknown | null;
  nationality: unknown | null;
  religion: unknown | null;
  corporateAgreementResponse: unknown[];
  isCreatedFromPatientMenu: boolean;
  externalMRN: string | null;
}

function toWireBody(input: CreatePatientInput): CreateUpdatePersonBody {
  return {
    email: input.email,
    isMissingData: false,
    importance: null,
    residencyType: null,
    // Constant Opt-In consent matches the working dev curl. A
    // dedicated consent picker would replace this if/when added.
    consent: { definition: 'Opt in', code: '0', display: 'Yes' },
    frontIdCardUrl: null,
    backIdCardUrl: null,
    phoneNumber: input.phoneNumber,
    isAdult: input.isAdult,
    organizationId: input.organizationId,
    patientId: null,
    practiceId: input.practiceId,
    firstName: input.firstName,
    middleName: input.middleName,
    lastName: input.lastName,
    birthDate: input.birthDate,
    age: null,
    gender: input.gender,
    genderName: input.genderName,
    languageName: null,
    timeZone: null,
    language: null,
    maritalStatus: null,
    interpreterRequired: false,
    photoUrl: null,
    idFrontImage: null,
    idBackImage: null,
    isActive: null,
    lastUpdated: null,
    patientMRN: null,
    identification: null,
    patientOccupation: null,
    patientEducation: null,
    guardianIdenitification: null,
    nationality: null,
    religion: null,
    corporateAgreementResponse: [],
    isCreatedFromPatientMenu: input.isCreatedFromPatientMenu,
    externalMRN: null,
  };
}

export function useCreatePatient() {
  return useMutation<CreatePatientResponse, Error, CreatePatientInput>({
    mutationFn: (input) =>
      post<CreatePatientResponse>(
        '/api/Patients/CreateUpdatePerson',
        toWireBody(input),
      ),
  });
}
